import type { GroupType } from "@/typings/customilyElements/elementsGroup";

export interface GroupLike {
    id: string | number;
    uuid?: string | null;
    type?: string | null;
    parentId?: string | number | null;
    [key: string]: any;
}

export interface ElementLike {
    uuid?: string | null;
    groupId?: string | number | null;
    [key: string]: any;
}

interface EpsLike {
    textsEps?: ElementLike[];
    circularTextsEps?: ElementLike[];
    imagePlaceHoldersEps?: ElementLike[];
    vectorEPSEps?: ElementLike[];
}

interface PreviewLike {
    textsPreview?: ElementLike[];
    circularTextsPreview?: ElementLike[];
    imagePlaceHoldersPreview?: ElementLike[];
    vectorEPSPreview?: ElementLike[];
}

export interface GroupIndex {
    byId: Map<GroupLike['id'], GroupLike>;
    byType: Record<GroupType, GroupLike[]>;
    byTypeAndUuid: Record<GroupType, Map<string, GroupLike>>;
}

export interface PreviewGroupSyncPatch {
    id: GroupLike['id'];
    parentId: GroupLike['id'] | null;
    position?: GroupLike['position'];
}

export interface SyncPreviewGroupsFromEpsResult {
    groups: GroupLike[];
    index: GroupIndex;
    createdPreviewGroupIds: GroupLike['id'][];
    orphanPreviewGroupIds: Set<GroupLike['id']>;
    previewAssignmentsByElementUuid: Map<string, GroupLike['id'] | null>;
}

export interface LoadedGroupsData {
    groupsToLoad: GroupLike[];
    groupsMapping: Record<string, GroupLike['id']>;
    groupReferenceToLoadedId: Map<GroupLike['id'] | string, GroupLike['id']>;
}

export interface CounterpartGroupSync {
    resolveTargetGroupId: (groupId: GroupLike['id'] | null | undefined) => GroupLike['id'] | null;
    syncTargetElementGroupEdit: (
        targetElement: ElementLike,
        prop: string,
        sourceGroupId: GroupLike['id'] | null | undefined
    ) => boolean;
}

function getGroupByReference(
    groupsById: Map<GroupLike['id'], GroupLike>,
    _groupsByTypeAndUuid: Record<GroupType, Map<string, GroupLike>> | undefined,
    groupRef: GroupLike['id'] | null | undefined,
    expectedType: GroupType
): GroupLike | undefined {
    if (groupRef == null || groupRef === '') {
        return undefined;
    }

    const groupById = groupsById.get(groupRef);
    if (groupById?.type === expectedType) {
        return groupById;
    }

    return undefined;
}

export function removeGroupsCollectionByType(groups: GroupLike[] = [], type: GroupType): GroupLike[] {
    return groups.filter((group) => group?.type !== type);
}

export function replaceGroupsCollectionByType(
    groups: GroupLike[] = [],
    type: GroupType,
    incomingGroups: GroupLike[] = []
): GroupLike[] {
    // 1) Keep all groups that are NOT of the target type.
    //    This preserves the opposite collection (eps or preview) exactly as-is.
    const preservedGroups = removeGroupsCollectionByType(groups, type);

    // 2) From the incoming collection, accept only groups of the requested type.
    //    This prevents accidental cross-type insertion.
    const normalizedIncomingGroups = (incomingGroups || []).filter((group) => group?.type === type);

    // 3) Build the final collection by replacing the target type entirely.
    //    Note: this function does not deduplicate by id/uuid; it trusts caller-provided incoming data.
    return [
        ...preservedGroups,
        ...normalizedIncomingGroups
    ];
}

/**
 * Builds the canonical in-memory indexes used by the group synchronization flow.
 *
 * The entire sync model depends on these invariants:
 * - EPS groups and Preview groups live in the same collection.
 * - EPS/Preview counterparts share the same uuid.
 * - Their ids differ, so all cross-step synchronization must resolve through uuid.
 */
export function createGroupIndex(groups: GroupLike[] = []): GroupIndex {
    const byId = new Map<GroupLike['id'], GroupLike>();
    const byType: Record<GroupType, GroupLike[]> = {
        eps: [],
        preview: []
    };
    const byTypeAndUuid: Record<GroupType, Map<string, GroupLike>> = {
        eps: new Map<string, GroupLike>(),
        preview: new Map<string, GroupLike>()
    };

    for (const group of groups) {
        byId.set(group.id, group);

        if (group.type !== 'eps' && group.type !== 'preview') {
            continue;
        }

        byType[group.type].push(group);

        if (group.uuid) {
            byTypeAndUuid[group.type].set(group.uuid, group);
        }
    }

    return {
        byId,
        byType,
        byTypeAndUuid
    };
}

/**
 * Returns true when a groupId points to an existing group of the expected type.
 */
export function isValidGroupIdForType(
    groupsById: Map<GroupLike['id'], GroupLike>,
    groupId: GroupLike['id'] | null | undefined,
    expectedType: GroupType,
    groupsByTypeAndUuid?: Record<GroupType, Map<string, GroupLike>>
): boolean {
    if (groupId == null || groupId === '') {
        return false;
    }

    return !!getGroupByReference(groupsById, groupsByTypeAndUuid, groupId, expectedType);
}

/**
 * Normalizes a groupId by type. Invalid or cross-type references are converted to null.
 */
export function normalizeGroupIdForType(
    groupsById: Map<GroupLike['id'], GroupLike>,
    groupId: GroupLike['id'] | null | undefined,
    expectedType: GroupType,
    groupsByTypeAndUuid?: Record<GroupType, Map<string, GroupLike>>
): GroupLike['id'] | null {
    const resolvedGroup = getGroupByReference(groupsById, groupsByTypeAndUuid, groupId, expectedType);
    return resolvedGroup ? resolvedGroup.id : null;
}

/**
 * Resolves a group reference to the expected type, accepting either:
 * - a direct id of the expected type, or
 * - an id of the counterpart type that shares the same uuid.
 *
 * This is useful when persisting data where incoming group ids may come from
 * mixed contexts (e.g. canvas state carrying preview ids while persisting EPS data).
 */
export function resolveGroupIdForTypeWithCounterpart(
    groupId: GroupLike['id'] | null | undefined,
    expectedType: GroupType,
    index: GroupIndex
): GroupLike['id'] | null {
    if (groupId == null || groupId === '') {
        return null;
    }

    const sourceGroup = index.byId.get(groupId);
    if (!sourceGroup) {
        return null;
    }

    if (sourceGroup.type === expectedType) {
        return sourceGroup.id;
    }

    if (!sourceGroup.uuid) {
        return null;
    }

    return index.byTypeAndUuid[expectedType].get(sourceGroup.uuid)?.id || null;
}

/**
 * Normalizes a list of elements so their groupId only points to groups of the expected type.
 */
export function normalizeElementGroupAssignments(
    elements: ElementLike[] = [],
    groupsById: Map<GroupLike['id'], GroupLike>,
    expectedType: GroupType,
    groupsByTypeAndUuid?: Record<GroupType, Map<string, GroupLike>>
): void {
    for (const element of elements) {
        element.groupId = normalizeGroupIdForType(groupsById, element?.groupId, expectedType, groupsByTypeAndUuid);
    }
}

/**
 * Normalizes parent references so groups only point to parents that exist and share the same type.
 */
export function normalizeGroupParentReferences(
    groups: GroupLike[] = [],
    groupsById: Map<GroupLike['id'], GroupLike>,
    _groupsByTypeAndUuid?: Record<GroupType, Map<string, GroupLike>>
): void {
    for (const group of groups) {
        const parentGroup = group.parentId != null ? groupsById.get(group.parentId) : null;
        if (!parentGroup || parentGroup.type !== group.type) {
            group.parentId = null;
        }
    }
}

/**
 * Flattens all EPS element collections into a single list.
 */
export function collectEpsElements(listEps: EpsLike[] = []): ElementLike[] {
    const elements: ElementLike[] = [];

    for (const eps of listEps) {
        elements.push(
            ...(eps?.textsEps || []),
            ...(eps?.circularTextsEps || []),
            ...(eps?.imagePlaceHoldersEps || []),
            ...(eps?.vectorEPSEps || [])
        );
    }

    return elements;
}

/**
 * Flattens all Preview element collections into a single list.
 */
export function collectPreviewElements(listPreview: PreviewLike[] = []): ElementLike[] {
    const elements: ElementLike[] = [];

    for (const preview of listPreview) {
        elements.push(
            ...(preview?.textsPreview || []),
            ...(preview?.circularTextsPreview || []),
            ...(preview?.imagePlaceHoldersPreview || []),
            ...(preview?.vectorEPSPreview || [])
        );
    }

    return elements;
}

/**
 * Sorts groups so parents are processed before children.
 */
export function sortGroupsParentsFirst<T extends GroupLike>(groups: T[] = []): T[] {
    return [...groups].sort((a, b) => {
        if (!a.parentId && b.parentId) return -1;
        if (a.parentId && !b.parentId) return 1;
        return 0;
    });
}

/**
 * Finds Preview groups that no longer have a matching EPS group by uuid.
 */
export function getOrphanPreviewGroupIds(index: GroupIndex): Set<GroupLike['id']> {
    const epsUuids = new Set(index.byType.eps.map((group) => group.uuid).filter(Boolean));

    return new Set(
        index.byType.preview.filter((group) => group.uuid && !epsUuids.has(group.uuid)).map((group) => group.id)
    );
}

/**
 * Resolves the preview parent id that corresponds to an EPS group's parent.
 *
 * The parent is valid only when:
 * - the EPS parent exists,
 * - it is also an EPS group,
 * - and there is a Preview counterpart with the same uuid.
 */
export function resolvePreviewParentIdFromEpsGroup(
    epsGroup: GroupLike,
    index: GroupIndex
): GroupLike['id'] | null {
    if (!epsGroup.parentId) {
        return null;
    }

    const epsParentGroup = index.byId.get(epsGroup.parentId);
    if (!epsParentGroup?.uuid || epsParentGroup.type !== 'eps') {
        return null;
    }

    return index.byTypeAndUuid.preview.get(epsParentGroup.uuid)?.id || null;
}

/**
 * Resolves the equivalent group id in another group type using shared uuid.
 */
export function resolveCounterpartGroupId(
    groupId: GroupLike['id'] | null | undefined,
    sourceType: GroupType,
    targetType: GroupType,
    index: GroupIndex
): GroupLike['id'] | null {
    if (groupId == null || groupId === '') {
        return null;
    }

    const sourceGroup = index.byId.get(groupId);
    if (!sourceGroup?.uuid || sourceGroup.type !== sourceType) {
        return null;
    }

    return index.byTypeAndUuid[targetType].get(sourceGroup.uuid)?.id || null;
}

/**
 * Creates helpers to mirror group assignments from one group type into another.
 *
 * This is used by incremental EPS -> Preview synchronization, where ProductService
 * needs to translate a source groupId and apply group-related edit props without
 * duplicating the resolution rules inline.
 */
export function createCounterpartGroupSync(
    getGroups: () => GroupLike[] = () => [],
    sourceType: GroupType,
    targetType: GroupType
): CounterpartGroupSync {
    const resolveTargetGroupId = (groupId: GroupLike['id'] | null | undefined): GroupLike['id'] | null =>
        resolveGroupIdForTypeWithCounterpart(groupId, targetType, createGroupIndex(getGroups()));

    const syncTargetElementGroupEdit = (
        targetElement: ElementLike,
        prop: string,
        sourceGroupId: GroupLike['id'] | null | undefined
    ): boolean => {
        if (prop === 'ungroup') {
            targetElement.groupId = null;
            return true;
        }

        if (prop === 'groupId') {
            targetElement.groupId = resolveTargetGroupId(sourceGroupId);
            return true;
        }

        return false;
    };

    return {
        resolveTargetGroupId,
        syncTargetElementGroupEdit
    };
}

/**
 * Creates the preview counterpart for an EPS group.
 */
export function createPreviewGroupFromEpsGroup(
    epsGroup: GroupLike,
    index: GroupIndex,
    createId: () => GroupLike['id']
): GroupLike {
    return {
        ...epsGroup,
        addedOnEdit: false,
        type: 'preview',
        id: createId(),
        parentId: resolvePreviewParentIdFromEpsGroup(epsGroup, index)
    };
}

/**
 * Creates an EPS group id -> Preview group id mapping using shared uuid.
 */
export function buildEpsToPreviewGroupIdMap(index: GroupIndex): Map<GroupLike['id'], GroupLike['id']> {
    const mapping = new Map<GroupLike['id'], GroupLike['id']>();

    for (const epsGroup of index.byType.eps) {
        if (!epsGroup.uuid) {
            continue;
        }

        const previewGroup = index.byTypeAndUuid.preview.get(epsGroup.uuid);
        if (previewGroup) {
            mapping.set(epsGroup.id, previewGroup.id);
        }
    }

    return mapping;
}

/**
 * Rewrites parentId references after groups have been re-created with new ids.
 *
 * This is used when loading Step 1 EPS groups into Step 2 Preview groups,
 * where preview ids are regenerated but hierarchy must be preserved.
 */
export function remapGroupsParentIds(
    groups: GroupLike[] = [],
    idMapping: Record<string, GroupLike['id']> = {}
): GroupLike[] {
    return groups.map((group) => ({
        ...group,
        parentId: group.parentId != null && idMapping[group.parentId] ? idMapping[group.parentId] : group.parentId
    }));
}

/**
 * Creates the group collection used when loading Step 2 and the references required
 * to translate legacy or EPS ids into loaded preview ids.
 */
export function createLoadedGroupsData(
    groups: GroupLike[] = [],
    loadFromStep1 = false,
    createId: () => GroupLike['id']
): LoadedGroupsData {
    const groupsMapping: Record<string, GroupLike['id']> = {};
    const groupsToLoad: GroupLike[] = [];
    const groupReferenceToLoadedId = new Map<GroupLike['id'] | string, GroupLike['id']>();
    const loadedGroupIdsByUuid = new Map<string, GroupLike['id']>();

    for (const group of groups || []) {
        const originalGroupId = group.id;
        const originalGroupUuid = group.uuid ?? null;

        if (loadFromStep1 && originalGroupUuid && loadedGroupIdsByUuid.has(originalGroupUuid)) {
            const existingLoadedGroupId = loadedGroupIdsByUuid.get(originalGroupUuid) as GroupLike['id'];

            groupsMapping[String(originalGroupId)] = existingLoadedGroupId;

            groupReferenceToLoadedId.set(originalGroupId, existingLoadedGroupId);

            continue;
        }

        const loadedGroupId = loadFromStep1 ? createId() : originalGroupId;

        if (loadFromStep1) {
            groupsMapping[String(originalGroupId)] = loadedGroupId;
        }

        groupsToLoad.push({
            ...group,
            id: loadedGroupId,
            ...(loadFromStep1 ? { type: 'preview' } : {})
        });

        groupReferenceToLoadedId.set(originalGroupId, loadedGroupId);
        if (originalGroupUuid && loadFromStep1) {
            loadedGroupIdsByUuid.set(originalGroupUuid, loadedGroupId);
        }
    }

    return {
        groupsToLoad: loadFromStep1 ? remapGroupsParentIds(groupsToLoad, groupsMapping) : groupsToLoad,
        groupsMapping,
        groupReferenceToLoadedId
    };
}

/**
 * Builds a lookup of EPS element uuid -> EPS group id for current assignments.
 */
export function mapEpsElementUuidToGroupId(listEps: EpsLike[] = []): Map<string, GroupLike['id']> {
    const epsElementGroupIdByUuid = new Map<string, GroupLike['id']>();

    for (const epsElement of collectEpsElements(listEps || [])) {
        if (!epsElement?.uuid || epsElement.groupId == null) {
            continue;
        }

        epsElementGroupIdByUuid.set(epsElement.uuid, epsElement.groupId as GroupLike['id']);
    }

    return epsElementGroupIdByUuid;
}

/**
 * Creates a resolver for element groupId values while loading Step 2.
 *
 * Resolution order:
 * - direct remap from loaded groups,
 * - remap from matching EPS element uuid,
 * - keep the id only if it already exists in the loaded group collection.
 */
export function createLoadedGroupIdResolver(
    groupsToLoad: GroupLike[] = [],
    groupsEps: GroupLike[] = [],
    listEps: EpsLike[] = [],
    groupsMapping: Record<string, GroupLike['id']> = {},
    groupReferenceToLoadedId: Map<GroupLike['id'] | string, GroupLike['id']> = new Map()
): (groupId: GroupLike['id'] | null | undefined, elementUuid?: string | null) => GroupLike['id'] | undefined {
    const epsGroupIdToPreviewGroupId = buildEpsToPreviewGroupIdMap(
        createGroupIndex([...(groupsEps || []), ...(groupsToLoad || [])])
    );
    const epsElementGroupIdByUuid = mapEpsElementUuidToGroupId(listEps);
    const loadedGroupIds = new Set(groupsToLoad.map((group) => group.id));

    return (groupId, elementUuid = null) => {
        if (groupId != null && groupId !== '') {
            const mappedGroupId =
                groupReferenceToLoadedId.get(groupId) ||
                groupsMapping[String(groupId)];
            if (mappedGroupId) {
                return mappedGroupId;
            }
        }

        if (elementUuid) {
            const epsGroupId = epsElementGroupIdByUuid.get(elementUuid);
            const mappedFromEpsGroupId = epsGroupId ? epsGroupIdToPreviewGroupId.get(epsGroupId) : null;
            if (mappedFromEpsGroupId) {
                return mappedFromEpsGroupId;
            }
        }

        if (groupId != null && groupId !== '') {
            if (loadedGroupIds.has(groupId)) {
                return groupId;
            }
        }

        return undefined;
    };
}

/**
 * Builds the set of parent/metadata updates that existing Preview groups should receive from EPS.
 */
export function buildPreviewGroupSyncPatches(index: GroupIndex): PreviewGroupSyncPatch[] {
    const patches: PreviewGroupSyncPatch[] = [];

    for (const epsGroup of index.byType.eps) {
        const previewGroup = epsGroup.uuid ? index.byTypeAndUuid.preview.get(epsGroup.uuid) : null;
        if (!previewGroup) {
            continue;
        }

        patches.push({
            id: previewGroup.id,
            parentId: resolvePreviewParentIdFromEpsGroup(epsGroup, index),
            position: epsGroup.position
        });
    }

    return patches;
}

/**
 * Produces the Preview group assignment that each preview element should have,
 * based on the current EPS element assignments.
 *
 * Elements are synchronized by uuid, while groups are translated through the
 * EPS -> Preview group mapping derived from group uuid.
 */
export function buildPreviewElementAssignmentsFromEps(
    listEps: EpsLike[] = [],
    index: GroupIndex
): Map<string, GroupLike['id'] | null> {
    const epsToPreviewGroupId = buildEpsToPreviewGroupIdMap(index);
    const previewAssignments = new Map<string, GroupLike['id'] | null>();

    for (const epsElement of collectEpsElements(listEps)) {
        if (!epsElement.uuid) {
            continue;
        }

        if (epsElement.groupId == null || epsElement.groupId === '') {
            previewAssignments.set(epsElement.uuid, null);
            continue;
        }

        const mappedPreviewGroupId = epsToPreviewGroupId.get(epsElement.groupId) || null;
        previewAssignments.set(epsElement.uuid, mappedPreviewGroupId);
    }

    return previewAssignments;
}

/**
 * Synchronizes Preview groups and element assignments from EPS state.
 *
 * This function:
 * - removes orphan Preview groups (missing EPS counterpart by uuid),
 * - creates missing Preview counterparts for EPS groups,
 * - syncs Preview hierarchy/metadata from EPS,
 * - reapplies Preview element group assignments by shared element uuid.
 */
export function syncPreviewGroupsFromEps(
    groups: GroupLike[] = [],
    listEps: EpsLike[] = [],
    listPreview: PreviewLike[] = [],
    createId: () => GroupLike['id']
): SyncPreviewGroupsFromEpsResult {
    let syncedGroups = groups;
    let groupIndex = createGroupIndex(syncedGroups);
    const orphanPreviewGroupIds = getOrphanPreviewGroupIds(groupIndex);

    if (orphanPreviewGroupIds.size) {
        syncedGroups = syncedGroups.filter((group) => !orphanPreviewGroupIds.has(group.id));

        clearRemovedPreviewGroupAssignments(listPreview, orphanPreviewGroupIds);

        groupIndex = createGroupIndex(syncedGroups);
        normalizeGroupParentReferences(syncedGroups, groupIndex.byId, groupIndex.byTypeAndUuid);
    }

    const createdPreviewGroupIds: GroupLike['id'][] = [];
    const groupsNeedingPreviewCounterpart = sortGroupsParentsFirst(
        groupIndex.byType.eps.filter((group) => {
            if (!group?.uuid) {
                return false;
            }

            const hasPreviewCounterpart = groupIndex.byTypeAndUuid.preview.has(group.uuid);

            return group.addedOnEdit || !hasPreviewCounterpart;
        })
    );

    for (const epsGroup of groupsNeedingPreviewCounterpart) {
        const previewGroup = createPreviewGroupFromEpsGroup(epsGroup, groupIndex, createId);

        syncedGroups.push(previewGroup);
        createdPreviewGroupIds.push(previewGroup.id);
        groupIndex = createGroupIndex(syncedGroups);

        epsGroup.addedOnEdit = false;
    }

    groupIndex = createGroupIndex(syncedGroups);

    for (const patch of buildPreviewGroupSyncPatches(groupIndex)) {
        const previewGroup = groupIndex.byId.get(patch.id);
        if (!previewGroup) {
            continue;
        }

        previewGroup.position = patch.position;
        previewGroup.parentId = patch.parentId;
    }

    const previewAssignmentsByElementUuid = buildPreviewElementAssignmentsFromEps(listEps, groupIndex);
    applyPreviewElementAssignments(listPreview, previewAssignmentsByElementUuid);

    return {
        groups: syncedGroups,
        index: createGroupIndex(syncedGroups),
        createdPreviewGroupIds,
        orphanPreviewGroupIds,
        previewAssignmentsByElementUuid
    };
}

/**
 * Clears preview element group references that point to removed preview groups.
 */
export function clearRemovedPreviewGroupAssignments(
    listPreview: PreviewLike[] = [],
    removedGroupIds: Set<GroupLike['id']>
): void {
    for (const previewElement of collectPreviewElements(listPreview)) {
        if (previewElement.groupId && removedGroupIds.has(previewElement.groupId)) {
            previewElement.groupId = null;
        }
    }
}

/**
 * Applies EPS-derived group assignments to preview elements by shared element uuid.
 */
export function applyPreviewElementAssignments(
    listPreview: PreviewLike[] = [],
    previewAssignmentsByElementUuid: Map<string, GroupLike['id'] | null>
): void {
    for (const previewElement of collectPreviewElements(listPreview)) {
        if (previewElement?.uuid && previewAssignmentsByElementUuid.has(previewElement.uuid)) {
            const assignedGroupId = previewAssignmentsByElementUuid.get(previewElement.uuid) ?? null;

            previewElement.groupId = assignedGroupId;
        }
    }
}

/**
 * Collects groups that are directly used by EPS and Preview elements.
 *
 * Cross-type references are ignored on purpose. EPS elements can only keep EPS groups,
 * and Preview elements can only keep Preview groups.
 */
export function collectUsedGroupIds(groups: GroupLike[], listEps: EpsLike[] = [], listPreview: PreviewLike[] = []): Set<GroupLike['id']> {
    const usedGroupIds = new Set<GroupLike['id']>();
    const groupIndex = createGroupIndex(groups);

    const collect = (elements: ElementLike[], expectedType: GroupType) => {
        for (const element of elements) {
            const normalizedGroupId = normalizeGroupIdForType(
                groupIndex.byId,
                element?.groupId,
                expectedType,
                groupIndex.byTypeAndUuid
            );
            if (normalizedGroupId != null) {
                usedGroupIds.add(normalizedGroupId);
            }
        }
    };

    collect(collectEpsElements(listEps), 'eps');
    collect(collectPreviewElements(listPreview), 'preview');

    return usedGroupIds;
}

/**
 * Starting from directly used groups, keeps only the ancestors required to preserve
 * hierarchy within the same group type.
 */
export function collectGroupIdsWithAncestors(
    seedGroupIds: Iterable<GroupLike['id']>,
    index: GroupIndex
): Set<GroupLike['id']> {
    const preservedGroupIds = new Set<GroupLike['id']>();
    const queue = Array.from(seedGroupIds);
    let queueIndex = 0;

    while (queueIndex < queue.length) {
        const currentGroupId = queue[queueIndex++];
        if (preservedGroupIds.has(currentGroupId)) {
            continue;
        }

        const currentGroup = index.byId.get(currentGroupId);
        if (!currentGroup) {
            continue;
        }

        preservedGroupIds.add(currentGroupId);

        const parentGroup = currentGroup.parentId ? index.byId.get(currentGroup.parentId) : null;
        if (parentGroup && parentGroup.type === currentGroup.type && !preservedGroupIds.has(parentGroup.id)) {
            queue.push(parentGroup.id);
        }
    }

    return preservedGroupIds;
}
